import { seo } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, Shield, RefreshCw } from "lucide-react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ApiService } from "@/services/api-service";

function generateCaptchaCode() {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
}

const formSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  email: z.string().email({ message: "Email is invalid" }),
  subject: z.string().optional(),
  message: z.string().min(1, { message: "Message is required" }),
  captcha: z.string().min(1, { message: "CAPTCHA is required" }),
});

type FormValues = z.infer<typeof formSchema>;

interface FaqItemProps {
  question: string;
  answer: string;
}

export const Route = createFileRoute("/_web/_layout/contact")({
  component: Contact,
  ssr: true,
  loader: async () => {
    const captchaCode = generateCaptchaCode();

    // Save this in session/store in a real implementation
    // For demo purposes, we'll just pass it to the client
    return {
      captchaCode,
    };
  },
  head: () => {
    const title = "Contact Us | Inkriv";
    const description =
      "Have questions or ideas? Reach out to the Inkriv team and let's connect.";
    const image =
      "https://res.cloudinary.com/dtcejk6h3/image/upload/v1747040895/og-image_ztxfrr.png";

    return {
      title,
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        ...seo({ title, description, image }),
      ],
    };
  },
});

function Contact() {
  const { captchaCode: initialCaptchaCode } = Route.useLoaderData();
  const [captchaCode, setCaptchaCode] = useState(initialCaptchaCode);
  const [captchaImage, setCaptchaImage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
      captcha: "",
    },
  });

  // Generate CAPTCHA SVG image
  useEffect(() => {
    // Create captcha image
    generateCaptchaImage(captchaCode);
  }, [captchaCode]);

  const generateCaptchaImage = (code: string) => {
    // Create a canvas-based SVG captcha image
    const svgWidth = 200;
    const svgHeight = 60;

    // Create the SVG
    let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">
      <rect width="100%" height="100%" fill="#f0f4ff" />`;

    // Add some random lines for noise
    for (let i = 0; i < 8; i++) {
      const x1 = Math.floor(Math.random() * svgWidth);
      const y1 = Math.floor(Math.random() * svgHeight);
      const x2 = Math.floor(Math.random() * svgWidth);
      const y2 = Math.floor(Math.random() * svgHeight);
      const color = `rgb(${Math.floor(Math.random() * 200)}, ${Math.floor(Math.random() * 200)}, ${Math.floor(Math.random() * 200)})`;
      svg += `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${color}" stroke-width="1" />`;
    }

    // Add dots for noise
    for (let i = 0; i < 50; i++) {
      const cx = Math.floor(Math.random() * svgWidth);
      const cy = Math.floor(Math.random() * svgHeight);
      const color = `rgb(${Math.floor(Math.random() * 200)}, ${Math.floor(Math.random() * 200)}, ${Math.floor(Math.random() * 200)})`;
      svg += `<circle cx="${cx}" cy="${cy}" r="1" fill="${color}" />`;
    }

    // Add the text
    const chars = code.split("");
    const charWidth = svgWidth / (chars.length + 1);

    chars.forEach((char, index) => {
      const x = charWidth * (index + 0.5);
      const y = svgHeight / 2 + Math.random() * 10 - 5;
      const fontSize = 24 + Math.floor(Math.random() * 8);
      const rotation = Math.random() * 30 - 15;
      const color = `rgb(${Math.floor(Math.random() * 100)}, ${Math.floor(Math.random() * 100)}, ${Math.floor(Math.random() * 200) + 55})`;

      svg += `<text x="${x}" y="${y}" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="bold" 
              fill="${color}" text-anchor="middle" transform="rotate(${rotation} ${x} ${y})">${char}</text>`;
    });

    svg += "</svg>";

    // Convert SVG to data URL
    const dataUrl = `data:image/svg+xml;base64,${btoa(svg)}`;
    setCaptchaImage(dataUrl);
  };

  const refreshCaptcha = () => {
    const newCaptchaCode = generateCaptchaCode();
    setCaptchaCode(newCaptchaCode);
    form.setValue("captcha", "");
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (data.captcha.toUpperCase() !== captchaCode) {
        setSubmitError("Incorrect CAPTCHA code. Please try again.");
        setIsSubmitting(false);
        refreshCaptcha();
        return;
      }

      await ApiService.contactSupport({
        name: data.name,
        email: data.email,
        subject: data.subject || undefined,
        message: data.message,
      });

      setSubmitted(true);
      form.reset();
    } catch (error: any) {
      console.error(error);
      setSubmitError(
        error.message || "Failed to send message. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    form.reset();
    refreshCaptcha();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-950 pt-10">
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-md md:text-xl font-bold mb-6 text-secondary-700">
              Get in Touch
            </h1>

            <p className="text-sm md:text-base font-satoshi text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Have questions, feedback, or want to learn more about Inkriv? We'd
              love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-8 px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mx-auto">
            {/* Contact Form */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
              <h2 className="text-2xl font-bold mb-6">Send Us a Message</h2>

              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.4 }}
                    className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg text-center"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        delay: 0.2,
                        type: "spring",
                        stiffness: 200,
                      }}
                    >
                      <MessageSquare className="h-12 w-12 text-green-500 mx-auto mb-4" />
                    </motion.div>
                    <h3 className="text-xl font-semibold mb-2">Thank You!</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      Your message has been sent successfully. We'll get back to
                      you as soon as possible.
                    </p>
                    <Button
                      onClick={resetForm}
                      variant="link"
                      className="text-purple-600 dark:text-purple-400 font-medium"
                    >
                      Send another message
                    </Button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Form {...form}>
                      <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-6"
                      >
                        <div className="grid md:grid-cols-2 gap-6">
                          <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Name</FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    className="bg-white dark:bg-gray-700"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Email</FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    type="email"
                                    className="bg-white dark:bg-gray-700"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <FormField
                          control={form.control}
                          name="subject"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Subject (Optional)</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  className="bg-white dark:bg-gray-700"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="message"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Message</FormLabel>
                              <FormControl>
                                <Textarea
                                  {...field}
                                  rows={6}
                                  className="bg-white dark:bg-gray-700"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* CAPTCHA Field */}
                        <motion.div
                          className="space-y-4"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 }}
                        >
                          <div className="flex items-center gap-2">
                            <Shield size={16} className="text-purple-500" />
                            <FormLabel className="text-sm font-medium">
                              Security Check
                            </FormLabel>
                          </div>

                          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-md">
                            <div className="mb-4 flex justify-center">
                              {captchaImage && (
                                <motion.div
                                  whileHover={{ scale: 1.02 }}
                                  className="relative overflow-hidden rounded-md shadow-sm"
                                >
                                  <img
                                    src={captchaImage}
                                    alt="CAPTCHA"
                                    className="max-w-full h-auto"
                                  />
                                </motion.div>
                              )}
                            </div>

                            <div className="flex gap-3">
                              <FormField
                                control={form.control}
                                name="captcha"
                                render={({ field }) => (
                                  <FormItem className="flex-1">
                                    <FormControl>
                                      <Input
                                        {...field}
                                        placeholder="Enter the code above"
                                        className="bg-white dark:bg-gray-700 text-xs"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={refreshCaptcha}
                                className="h-10 w-10 flex-shrink-0"
                              >
                                <RefreshCw size={16} />
                              </Button>
                            </div>
                          </div>
                        </motion.div>

                        {submitError && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg text-red-600 dark:text-red-400"
                          >
                            {submitError}
                          </motion.div>
                        )}

                        <motion.div
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full bg-secondary-700 hover:bg-secondary-500 text-white font-medium py-3 px-6 rounded-lg 
                              transition-all duration-200"
                          >
                            {isSubmitting ? (
                              <div className="flex items-center font-satoshi">
                                <motion.div
                                  animate={{ rotate: 360 }}
                                  transition={{
                                    duration: 1,
                                    repeat: Infinity,
                                    ease: "linear",
                                  }}
                                  className="mr-2"
                                >
                                  <RefreshCw size={16} />
                                </motion.div>
                                Sending...
                              </div>
                            ) : (
                              "Send Message"
                            )}
                          </Button>
                        </motion.div>
                      </form>
                    </Form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-12 text-center">
            Frequently Asked Questions
          </h2>

          <Accordion type="single" collapsible className="space-y-6">
            <FaqItem
              question="How is Inkriv different from other AI writing tools?"
              answer="Most tools generate text. Inkriv learns your voice continuously, so everything you write sounds more like you over time. It also cites real sources and works with your files, links, and even voice notes."
            />
            <FaqItem
              question="Can I use both autocomplete and full draft generation?"
              answer="Yes. You can write line-by-line and get in-context suggestions, or provide input (notes, links, voice memos) and let Inkriv ghostwrite an entire article."
            />
            <FaqItem
              question="What formats can I upload for research?"
              answer="You can upload PDFs, Word documents, paste in links, or speak a voice memo. Inkriv extracts relevant points and sources from each."
            />
            <FaqItem
              question="Is my writing and data private?"
              answer="Yes. All files, inputs, and drafts are encrypted. Your content is never shared or reused and your tone model is unique to you."
            />
            <FaqItem
              question="What happens on the free plan?"
              answer="You can generate up to 7500 words, upload 3 documents for personalisation, use 15 minutes of voice input per month, and try a limited version of our chat assistant. Upgrade anytime to unlock full features."
            />
          </Accordion>
        </div>
      </section>
    </div>
  );
}

function FaqItem({ question, answer }: FaqItemProps) {
  return (
    <AccordionItem
      value={question}
      className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
    >
      <AccordionTrigger className="px-4 py-3 font-medium">
        {question}
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4 text-sm font-satoshi text-gray-600 dark:text-gray-300 ">
        {answer}
      </AccordionContent>
    </AccordionItem>
  );
}
